import * as React from 'react';
import { withTranslation } from 'react-i18next';
import {
    Badge,
    Button,
    ButtonGroup,
    FormGroup,
    UncontrolledTooltip,
} from 'reactstrap';
import { debounce } from 'lodash-es';
import cls from 'classnames';
import AsyncSelect from 'react-select/async';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAmbulance, faArrowUpRightFromSquare, faCaretRight, faTimesCircle, faInfoCircle,
    faCheck,
    faPenToSquare,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from '@s20/components/common/Link';
import { getTimezones } from '@s20/common/util/l10n';
import { httpClient } from '@s20/services/httpClient';
import {
    ApiRoutes, ApplicationPermissions, AppNavPaths, CustomFormContext,
} from '../Common';
import {
    Flex, FormCheckbox, FormLabel, Input, Textarea, toasty,
} from '../common/forms/FormElements';
import { Contract } from '../contracts/Contract';
import {
    RejectionNote,
    TimesheetStatus,
    TimesheetAuditType,
} from '../timesheet/Timesheet';
import { WorkOrderTypes } from '../workOrder/WorkOrder';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { formatDateTimeForTimezone } from '../dispatch/Dispatch';
import { Can } from '../Can';
import { Modal } from '../common/Modal';
import CustomForms from '../customForm/CustomForms';


export class Job {
    contractId = 0;

    contractSequence = 0;

    weekOfDate = '';

    requestedById = 0;

    foremanId = 0;

    startTime = '';

    dispatchOfficeLocationId = '';

    dispatchOfficeLocationName = '';

    dispatchPortId = null;

    dispatchPortName = '';

    workOrderNumber = '';

    workRequest = '';

    purchaseOrderNumber = '';

    jobCity = '';

    notes = '';

    resources = [];

    nonResources = [];

    overrides = [];

    tempSessionId = null;

    contract = new Contract();

    meetingLocation = null; // Address()

    jobLocation = null; // Address()

    hasAssignments = false;

    tenantSettings = null;

    jobNumber = null;

    jobDate = new Date();

    jobTypeId = null;

    selectedResourceKits = [];

    isEmergency = false;

    isQuickRental = false;
}

export const JobTypes = {
    Historic: 1,
};
export const toContactDropdownOption = (locationContact) => {
    const { id } = locationContact;

    const contactName = locationContact.title
        ? `${locationContact.lastName}, ${locationContact.firstName} (${locationContact.title})`
        : `${locationContact.lastName}, ${locationContact.firstName}`;

    return {
        id,
        contactName,
    };
};
class JobDetailsBase extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            workOrderNumberValid: true,
            foremanValid: true,
            isCallingOutAssignment: false,
            isCancellingJob: false,
            showCancelJobModal: false,
        };
    }

    onClientCancel = async (values) => {
        const {
            timesheetId,
            timesheetNumber,
            onRefresh,
            jobId,
            jobStartTimeRaw,
        } = { ...this.props };

        const {
            selectedJobCancellationTime,
            selectedJobCancellationNotes,
            selectedJobCancellationReasonType,
            cancelContactId,
            isCrewOnSiteAndWorkPerformed,
        } = values;

        if (parseInt(selectedJobCancellationReasonType ?? 0) <= 0) {
            toasty.error('Please select the cancellation reason.');
            return false;
        }

        const jobDate = new Date(jobStartTimeRaw);
        const JobDay = jobDate.getDay();

        const jobCancelTime = moment.tz(selectedJobCancellationTime, this.getJobTimeZone()).format();

        const model = {
            jobId,
            day: JobDay,
            cancellationTypeId: 1, // client cancel only
            cancellationReasonTypeId: selectedJobCancellationReasonType,
            cancellationNote: selectedJobCancellationNotes,
            cancellationTime: jobCancelTime,
            sendNotifications: false,
            cancelContactId,
            isCrewOnSiteAndWorkPerformed,
        };

        this.setState({ isCancellingJob: true });

        const response = await httpClient.post(
            ApiRoutes.dispatch.cancelJob(),
            model,
        );

        if (response === 'ok') {
            await httpClient.put(ApiRoutes.timesheet.audit(), {
                timesheetId,
                timesheetNumber,
                timesheetAuditTypeId: TimesheetAuditType.ClientCancelApplied,
                systemMessage: jobCancelTime,
                systemTime: jobCancelTime,
                userNotes: selectedJobCancellationNotes,
            });

            toasty.success('Client Cancellation Successful');
            onRefresh();
        } else {
            toasty.error('Client Cancellation Error');
        }
    };

    onSelectedJobCancellationReasonChanged = (selection) => {
        const reasonTypeSelection = { ...selection };
        const selectedJobCancellationReasonType = reasonTypeSelection.value;
        this.setState({ selectedJobCancellationReasonType });
    };
    getJobTimeZone = () => {
        const { jobTimeZone } = { ...this.props };

        if (jobTimeZone != null) {
            const { ianaTimezones } = getTimezones();
            const timeZone = ianaTimezones?.find((tz) => tz.id === jobTimeZone || tz.value === jobTimeZone)?.value;
            return timeZone;
        }
    };
    getCorrectTime = (dateTime) => formatDateTimeForTimezone(dateTime, this.getJobTimeZone());
    onEmergencyAssignment = async () => {
        this.setState({ isCallingOutAssignment: true });
        const {
            onRefresh,
            timesheetId,
            isEmergency,
            isBillingEmergency,
            isPayrollEmergency,
            isPayroll,
            isBilling,
            isTimesheet,
        } = this.props;

        let isEmergencyOverall = false;
        if (isTimesheet && isEmergency) isEmergencyOverall = true;
        if (isBilling && isBillingEmergency) isEmergencyOverall = true;
        if (isPayroll && isPayrollEmergency) isEmergencyOverall = true;

        const model = {
            timesheetId,
            isEmergency: !isEmergencyOverall,
        };
        let route = ApiRoutes.timesheet.setEmergency();
        if (isPayroll) route = ApiRoutes.payrollManagement.setEmergency();
        else if (isBilling) route = ApiRoutes.billing.setEmergency();

        const response = await httpClient.post(route, model);

        if (response.success) {
            onRefresh(); // refreshes reused
        } else {
            toasty.warning('Error adjusting emergency status.', {
                autoClose: false,
                closeOnClick: true,
            });
        }

        this.setState({
            isCallingOutAssignment: false,
            selectedEmergencyAssignments: null,
        });
    };

    onUndoClientCancel = async () => {
        const { timesheetId, timesheetNumber, onRefresh } = { ...this.props };

        let success = false;
        try {
            const response = await httpClient.put(
                ApiRoutes.timesheet.undoClientCancel(),
                timesheetId,
                httpClient.format.none,
            );

            success = !!response.ok;
        } catch {
            success = false;
        }

        if (success) {
            await httpClient.put(ApiRoutes.timesheet.audit(), {
                timesheetId,
                timesheetNumber,
                timesheetAuditTypeId: TimesheetAuditType.ClientCancelRemoved,
                systemMessage: '',
                systemTime: null,
                userNotes: '',
            });

            toasty.success('Client Cancellation Undone.');
            onRefresh();
        } else {
            toasty.error(
                'There was an error undoing the client cancel. Please try your request again.',
            );
        }
    };

    loadOptions = debounce((inputValue, callback) => {
        fetch(ApiRoutes.typeAheads.jobCities(inputValue))
            .then((response) => response.json())
            .then((data) => callback(data));
    }, 250);

    showUndoClientCancelModal = () => {
        Modal.show({
            title: 'Undo Client Cancel',
            onSubmit: () => this.onUndoClientCancel(),
            body: () => 'Are you sure you would like to undo the client cancel?',
        });
    };

    getIsEmergency = () => {
        const {
            isTimesheet, isEmergency, isBilling, isBillingEmergency,
            isPayroll, isPayrollEmergency,
        } = this.props;

        let isEmergencyOverall = false;
        if (isTimesheet && isEmergency) isEmergencyOverall = true;
        if (isBilling && isBillingEmergency) isEmergencyOverall = true;
        if (isPayroll && isPayrollEmergency) isEmergencyOverall = true;

        return isEmergencyOverall;
    };

    showEmergencyConfirmModal = () => {
        const { isSelfDispatching } = this.props;

        if (isSelfDispatching) return false;

        Modal.show({
            title: 'Emergency confirmation',
            onSubmit: () => this.onEmergencyAssignment(),
            body: () => this.getIsEmergency()
                ? 'Are you sure you would like to remove emergency work from this job?'
                : 'Are you sure you would like to set this job as emergency work?',
        });
    };

    showCancelJobModal = async () => {
        const { jobStartTime, isExpandedCancellationReasonRequired, jobId } = this.props;

        const selectedJobCancellationType = 1; // client cancel
        let companyContacts = [];
        if (isExpandedCancellationReasonRequired) {
            const allCompanyContacts = await httpClient.post(
                ApiRoutes.locationContact.forJob(jobId),
            );
            companyContacts = allCompanyContacts.map((lc) => toContactDropdownOption(lc));
        }
        const jobCancellationReasonTypes = await httpClient.js(
            ApiRoutes.typeAheads.getCancelReasonsByCancellationTypeId(selectedJobCancellationType),
        );
        Modal.show({
            title: 'Client Cancellation',
            onSubmit: (values) => this.onClientCancel(values),
            body: () => (
                <>
                    <FormGroup>
                        <label>{`Job Start Time: ${jobStartTime}`}</label>
                    </FormGroup>

                    <ValidatedSelect
                        placeholder="Select Cancellation Reason"
                        label="Cancellation Reason"
                        id="selectedJobCancellationReasonType"
                        name="selectedJobCancellationReasonType"
                        isClearable={false}
                        required
                        options={jobCancellationReasonTypes}
                        error="A cancellation reason is required"
                    />
                    {isExpandedCancellationReasonRequired && (
                        <ValidatedSelect
                            isClearable={false}
                            id="cancelContactId"
                            name="cancelContactId"
                            label="Cancel Requested By"
                            required
                            options={companyContacts}
                            getOptionLabel={(locationContact) => locationContact.contactName}
                            getOptionValue={(locationContact) => locationContact.id}
                            valueField="id"
                            error="Please select who requested this cancellation"
                        />
                    )}
                    <Input
                        label="Cancellation Time"
                        id="selectedJobCancellationTime"
                        required
                        type="datetime-local"
                        className="form-control"
                        name="selectedJobCancellationTime"
                        error="Cancellation Time is required."
                    />
                    <FormCheckbox
                        small
                        id="isCrewOnSiteAndWorkPerformed"
                        name="isCrewOnSiteAndWorkPerformed"
                        onChange={this.onChange}
                        labelText="Crew arrived on site and performed work"
                    />

                    <Textarea
                        label="Notes"
                        id="notes"
                        name="selectedJobCancellationNotes"
                        placeholder="Enter optional notes regarding the cancellation."
                        maxLength="500"
                        rows="5"
                    />
                </>
            ),
        });
    };
    render() {
        const { workOrderNumberValid } = { ...this.state };

        const {
            customerName,
            dispatchOfficeName,
            subcontractorName,
            enableWorkOrderNumberEditing,
            customerContactsOptions,

            enableForemanEditing,
            foremanChanged,
            foremanId,
            initialForemanId,
            foremanName,
            foremanPhone,

            enableRequestorEditing,
            requestorChanged,
            requestorId,
            initialRequestorId,
            requestorName,
            requestorPhone,
            jobId,
            jobNotes,
            jobNumber,
            jobCity,
            jobStartTime,
            jobStartTimeRaw,
            lastRejectionNote,
            onWorkOrderChanged,
            onWorkRequestChanged,
            onPOChanged,
            onJobCityChanged,
            permissions = [],
            timesheetNumber,
            timesheetId,
            timesheetStatus,
            statusRejectionNote,
            projectId,
            workOrderNumber,
            initialWorkOrderNumber,
            workOrderNumberChanged,
            workOrderType,
            isClientCancelled,
            isSchedulingErrorCancelled,
            emergencyEmployee,
            timesheetStatusId,
            isTimesheetForm,
            tenantSettings,
            isTimesheet,
            isPayroll,
            isBilling,
            billingEmergencyEmployee,
            payrollEmergencyEmployee,
            billingStatusId,
            payrollStatusId,
            canEmergency,
            isSelfDispatching,
            isNonFlagging,
            isAdmin,
            jobName,

            jobTypeOptions,
            enableJobTypeEditing,
            jobTypeId,
            initialJobTypeId,
            jobTypeChanged,
            cancelledReasonType,
            workRequest,
            workRequestChanged,
            purchaseOrderNumber,
            purchaseOrderNumberChanged,
            workRequestRequired,
            purchaseOrderNumberRequired,
            initialPurchaseOrderNumber,
            initialWorkRequest,
            onInlineWorkOrderNumberSubmit,
            showCustomForms,
            isEmergencyJob,
            t,
            isJobCancelled,
        } = { ...this.props };

        const workOrderNA = workOrderType === WorkOrderTypes.NotApplicable;
        const requireWorkOrder = workOrderType === WorkOrderTypes.Required;
        const customerNameFormatted = (customerName ?? '')
            + (subcontractorName ? ` - ${subcontractorName}` : '');
        const foremanNameFormatted = (foremanName ?? '-') + (foremanPhone ? ` - ${foremanPhone}` : '');
        const requestorNameFormatted = (requestorName ?? '-')
            + (requestorPhone ? ` - ${requestorPhone}` : '');

        let emergencyEmployeeName = emergencyEmployee;
        if (isPayroll && payrollEmergencyEmployee) {
            emergencyEmployeeName = payrollEmergencyEmployee;
        }
        if (isBilling && billingEmergencyEmployee) {
            emergencyEmployeeName = billingEmergencyEmployee;
        }

        const timesheetUseJobCity = tenantSettings
            ? tenantSettings.timesheetUseJobCity
            : false;

        const showContractDescriptionInTimesheetView = Boolean(
            tenantSettings
            && tenantSettings.showContractDescriptionInTimesheetView
            && jobName,
        );

        const disableEdit =
            timesheetStatusId === TimesheetStatus.Approved &&
            !permissions.includes(ApplicationPermissions.timesheet_edit_approved);

        const isEmergencyOverall = this.getIsEmergency();

        return (
            <div className="job-confirm-details">
                {!!timesheetNumber && (
                    <Flex alignItems="center">
                        <FormLabel
                            htmlFor="timesheetNumber"
                            label="Timesheet #"
                        />

                        <span
                            id="timesheetNumber"
                            name="timesheetNumber"
                            className="text-muted"
                            style={{ marginLeft: '16px' }}
                        >
                            {timesheetNumber ?? ''}
                        </span>

                        {!!isSelfDispatching && (
                            <Badge
                                color="info"
                                className="ml-2"
                                title={
                                    isNonFlagging
                                        ? 'This is a non-flagging timesheet'
                                        : 'This is a self-dispatching timesheet'
                                }
                                style={{
                                    fontSize: '1em',
                                    marginTop: '-2px',
                                    padding: '2px 4px',
                                }}
                            >
                                {isNonFlagging ? 'NF' : 'SD'}
                            </Badge>
                        )}

                        <Link
                            to={`${AppNavPaths.TimesheetManagement}?t=${timesheetId}`}
                            target="_blank"
                            className="ml-1"
                        >
                            <FontAwesomeIcon
                                icon={faArrowUpRightFromSquare}
                                size="sm"
                            />
                        </Link>
                    </Flex>
                )}
                {!!timesheetStatus && (
                    <Flex alignItems="center">
                        <FormLabel
                            htmlFor="timesheetStatus"
                            label="Status"
                        />
                        {timesheetStatus !== 'Rejected' && (
                            <span
                                className={`timesheet-status-label ${(
                                    timesheetStatus ?? ''
                                )
                                    .replace(' ', '')
                                    .toLowerCase()}`}
                            >
                                {timesheetStatus}
                            </span>
                        )}
                        {timesheetStatus === 'Rejected'
                            && !!lastRejectionNote && (
                                <div style={{ marginLeft: '20px' }}>
                                    <RejectionNote
                                        note={lastRejectionNote}
                                    />
                                </div>
                            )}
                        {timesheetStatus === 'Billing-Payroll Rejected'
                            && !!statusRejectionNote && (
                                <div style={{ marginLeft: '20px' }}>
                                    {!!statusRejectionNote && (
                                        <RejectionNote
                                            note={statusRejectionNote}
                                        />
                                    )}
                                </div>
                            )}
                        {timesheetStatus === 'Operations Review'
                            && !!lastRejectionNote && (
                                <div style={{ marginLeft: '20px' }}>
                                    <RejectionNote
                                        note={lastRejectionNote}
                                    />
                                </div>
                            )}
                        {timesheetStatus === 'Rejected'
                            && !lastRejectionNote && (
                                <div
                                    style={{ marginLeft: '20px' }}
                                    className="site-alert alert-danger text-center"
                                >
                                    REJECTED
                                </div>
                            )}

                        {isClientCancelled && (
                            <>
                                <span className="timesheet-status-label">
                                    {' '}
                                    -
                                    {' '}
                                </span>
                                <span
                                    className="timesheet-status-label form-control-sm client-cancel"
                                >
                                    Client Cancel
                                </span>
                                {cancelledReasonType && (
                                    <>
                                        <FontAwesomeIcon
                                            id={`reasonType-${jobId}`}
                                            icon={faInfoCircle}
                                            size="md"
                                            className="ml-1"
                                        />
                                        <UncontrolledTooltip
                                            key={`tooltip-${jobId}`}
                                            placement="top"
                                            target={`reasonType-${jobId}`}
                                            delay={{ show: 0, hide: 0 }}
                                        >
                                            {cancelledReasonType}
                                        </UncontrolledTooltip>
                                    </>
                                )}
                            </>
                        )}

                        {isSchedulingErrorCancelled && (
                            <>
                                <span className="timesheet-status-label">
                                    {' '}
                                    -
                                    {' '}
                                </span>
                                <span
                                    className="timesheet-status-label form-control-sm client-cancel"
                                >
                                    Scheduling Error
                                </span>
                            </>
                        )}
                    </Flex>
                )}
                <Flex alignItems="center">
                    <FormLabel
                        htmlFor="jobStartTime"
                        label={t('timesheet:dateStart')}
                    />
                    <span
                        id="jobStartTime"
                        name="jobStartTime"
                    >
                        {jobStartTime ?? ''}
                    </span>
                </Flex>


                <Flex alignItems="center">
                    <FormLabel
                        htmlFor="customerName"
                        label={t('timesheet:customer')}
                    />
                    <span
                        id="customerName"
                        name="customerName"
                    >
                        {customerNameFormatted ?? ''}
                    </span>
                </Flex>

                <Flex alignItems="center">
                    <FormLabel
                        htmlFor="dispatchOfficeName"
                        label={t('timesheet:dispatch')}
                    />
                    <span
                        id="dispatchOfficeName"
                        name="dispatchOfficeName"
                    >
                        {dispatchOfficeName}
                    </span>
                </Flex>
                {showContractDescriptionInTimesheetView && (
                    <Flex alignItems="center">
                        <FormLabel
                            htmlFor="jobName"
                            label={t('timesheet:contractLabel')}
                        />
                        <span
                            id="jobName"
                            name="jobName"
                        >
                            {jobName ?? ''}
                        </span>
                    </Flex>
                )}
                {!isSelfDispatching && (
                    <Flex alignItems="center">
                        <FormLabel
                            htmlFor="jobNumber"
                            label={t('timesheet:jobLabel')}
                        />
                        <span
                            id="jobNumber"
                            name="jobNumber"
                        >
                            {jobNumber ?? ''}

                            {Boolean(jobNumber) && Boolean(jobId) && (
                                <Can
                                    I="edit"
                                    a="job"
                                >
                                    <Link
                                        to={projectId ? `${AppNavPaths.Project}/${projectId}?openJob=${jobId}` : `${AppNavPaths.Job}/${jobId}`}
                                        target="_blank"
                                        className="ml-1"
                                    >
                                        <FontAwesomeIcon
                                            icon={faArrowUpRightFromSquare}
                                            size="sm"
                                        />
                                    </Link>
                                </Can>
                            )}
                        </span>
                    </Flex>
                )}

                {!isSelfDispatching && timesheetUseJobCity && (
                    <Flex alignItems="center">
                        <FormLabel
                            htmlFor="jobCity"
                            label="Job City"
                        />
                        <div className="adj-indicator" />
                        <div className="d-flex flex-column">
                            {Boolean(enableWorkOrderNumberEditing) &&
                                permissions &&
                                permissions.includes(ApplicationPermissions.job_edit) && (
                                    disableEdit === false ? (
                                        <ValidatedSelect
                                            component={AsyncSelect}
                                            loadOptions={this.loadOptions}
                                            placeholder="Type City Name Here"
                                            id="jobCity"
                                            name="jobCity"
                                            value={
                                                jobCity
                                                    ? { label: jobCity, value: jobCity }
                                                    : ''
                                            }
                                            onChange={onJobCityChanged}
                                        />
                                    ) : (
                                        <span>
                                            {jobCity || 'None selected'}
                                        </span>
                                    )
                                )}
                        </div>
                    </Flex>
                )}
                {
                    /* TODO - Check for contract setting for work orders on self-dispatching timesheets */
                    !isSelfDispatching && (
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="workOrderNumber"
                                label={t('timesheet:workOrderHashLabel')}
                            />
                            <div className="adj-indicator">
                                {!!workOrderNumberChanged && (
                                    <Badge
                                        title="This work order # has been adjusted."
                                        color="warning"
                                        className="adj-badge"
                                        style={{
                                            fontSize: '100% !important',
                                        }}
                                    >
                                        ADJ
                                    </Badge>
                                )}
                            </div>
                            {
                                // Read only fields when WON editing is off.
                                (!enableWorkOrderNumberEditing
                                    || !!workOrderNA) && (
                                    <div className="d-flex flex-column">
                                        <span
                                            id="workOrderNumber"
                                            name="workOrderNumber"
                                        >
                                            {workOrderNumber ?? '-'}
                                        </span>
                                        {!!workOrderNumberChanged && (
                                            <small className="mt-1">
                                                <strong className="mr-1 text-muted">
                                                    Initial Work Order:
                                                </strong>
                                                {`${initialWorkOrderNumber || 'N/A'}`}
                                            </small>
                                        )}
                                    </div>
                                )
                            }
                            {
                                // Read only field if no permission, otherwise provide input.
                                !!enableWorkOrderNumberEditing && (
                                    <>
                                        {(!permissions || !permissions.includes(
                                            ApplicationPermissions.job_edit,
                                        ))
                                            && !workOrderNA && (
                                                <div className="d-flex flex-column">
                                                    <span
                                                        id="workOrderNumber"
                                                        name="workOrderNumber"
                                                    >
                                                        {workOrderNumber
                                                            ?? '-'}
                                                    </span>
                                                    {!!workOrderNumberChanged && (
                                                        <small className="mt-1">
                                                            <strong className="mr-1 text-muted">
                                                                Initial Work
                                                                Order:
                                                            </strong>
                                                            {`${initialWorkOrderNumber || 'N/A'}`}
                                                        </small>
                                                    )}
                                                </div>
                                            )}
                                        {!!(permissions && permissions.includes(
                                            ApplicationPermissions.job_edit,
                                        ))
                                            && !workOrderNA && (
                                                <div className="d-flex flex-column">
                                                    <Flex alignItems="center">
                                                        {onInlineWorkOrderNumberSubmit ? (
                                                            <>
                                                                {this.state.isEditingWorkOrderNumber ? (
                                                                    <div style={{ minWidth: 400 }}>
                                                                        <Input
                                                                            name="overrideWorkOrderNumber"
                                                                            value={this.state.overrideWorkOrderNumber ?? workOrderNumber}
                                                                            onChange={(e) => this.setState({ overrideWorkOrderNumber: e.target.value })}
                                                                            endAddonContained={false}
                                                                            endAddon={(
                                                                                <ButtonGroup>
                                                                                    <Button
                                                                                        color="light"
                                                                                        onClick={() => this.setState({
                                                                                            isEditingWorkOrderNumber: false,
                                                                                            overrideWorkOrderNumber: workOrderNumber,
                                                                                        })}
                                                                                    >
                                                                                        Cancel
                                                                                    </Button>
                                                                                    <Button
                                                                                        color="primary"
                                                                                        onClick={() => onInlineWorkOrderNumberSubmit(this.state.overrideWorkOrderNumber ?? workOrderNumber)}
                                                                                        className="btn btn-primary d-flex align-items-center"
                                                                                    >
                                                                                        <div className="d-flex align-items-center">
                                                                                            <FontAwesomeIcon icon={faCheck} />
                                                                                            <span className="ml-2">Save</span>
                                                                                        </div>
                                                                                    </Button>
                                                                                </ButtonGroup>
                                                                            )}
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <span className="d-inline-flex align-items-center">
                                                                        <span className="text-muted">{workOrderNumber}</span>

                                                                        <Button
                                                                            color="link"
                                                                            className="p-0 ml-2"
                                                                            aria-label="Edit work order number"
                                                                            onClick={() => this.setState({ isEditingWorkOrderNumber: true })}
                                                                        >
                                                                            <FontAwesomeIcon icon={faPenToSquare} />
                                                                        </Button>
                                                                    </span>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <Input
                                                                autoComplete="off"
                                                                id="workOrderNumber"
                                                                maxLength="44"
                                                                displayMaxLength={false}
                                                                required={
                                                                    requireWorkOrder
                                                                }
                                                                disabled={
                                                                    disableEdit
                                                                }
                                                                name="workOrderNumber"
                                                                className={cls(
                                                                    'p-1 form-control-sm w-auto',
                                                                    {
                                                                        'border-danger':
                                                                            !workOrderNumberValid,
                                                                    },
                                                                )}
                                                                value={
                                                                    workOrderNumber
                                                                    ?? ''
                                                                }
                                                                onChange={
                                                                    onWorkOrderChanged
                                                                }
                                                            />
                                                        )}
                                                    </Flex>

                                                    {!workOrderNumberValid && (
                                                        <small className="ml-3 pb-2 invalid-feedback text-danger d-flex">
                                                            Contract
                                                            requires a work
                                                            order number.
                                                        </small>
                                                    )}
                                                    {!!workOrderNumberChanged && (
                                                        <small className="mt-1">
                                                            <strong className="mr-1 text-muted">
                                                                Initial Work
                                                                Order:
                                                            </strong>
                                                            {`${initialWorkOrderNumber || 'N/A'}`}
                                                        </small>
                                                    )}
                                                </div>
                                            )}
                                    </>
                                )
                            }
                        </Flex>
                    )
                }
                {
                    !isSelfDispatching && workRequestRequired && (
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="workRequest"
                                label="Work Request"
                            />
                            <div className="adj-indicator">
                                {!!workRequestChanged && (
                                    <Badge
                                        title="This work request # has been adjusted."
                                        color="warning"
                                        className="adj-badge"
                                        style={{
                                            fontSize: '100% !important',
                                        }}
                                    >
                                        ADJ
                                    </Badge>
                                )}
                            </div>
                            {(!permissions || !permissions.includes(ApplicationPermissions.job_edit)) && (
                                <div className="d-flex flex-column">
                                    <span
                                        id="workRequest"
                                        name="workRequest"
                                    >
                                        {workRequest ?? '-'}
                                    </span>
                                    {!!workRequestChanged && (
                                        <small className="mt-1">
                                            <strong className="mr-1 text-muted">
                                                Initial Work
                                                Request:
                                            </strong>
                                            {`${initialWorkRequest}`}
                                        </small>
                                    )}
                                </div>
                            )}
                            {!!(permissions && permissions.includes(ApplicationPermissions.job_edit)) && (
                                <div className="d-flex flex-column">
                                    <Flex alignItems="center">
                                        <Input
                                            autoComplete="off"
                                            id="workRequest"
                                            maxLength="44"
                                            displayMaxLength={false}
                                            disabled={
                                                disableEdit
                                            }
                                            className={cls(
                                                'p-1 form-control-sm w-auto',
                                            )}
                                            name="workRequest"
                                            value={
                                                workRequest
                                                ?? ''
                                            }
                                            onChange={
                                                onWorkRequestChanged
                                            }
                                        />
                                    </Flex>
                                    {!!workRequestChanged && (
                                        <small className="mt-1">
                                            <strong className="mr-1 text-muted">
                                                Initial Work
                                                Request:
                                            </strong>
                                            {`${initialWorkRequest}`}
                                        </small>
                                    )}
                                </div>
                            )}
                        </Flex>
                    )
                }
                {
                    !isSelfDispatching && purchaseOrderNumberRequired && (
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="purchaseOrderNumber"
                                label="PO #"
                            />
                            <div className="adj-indicator">
                                {!!purchaseOrderNumberChanged && (
                                    <Badge
                                        title="This PO # has been adjusted."
                                        color="warning"
                                        className="adj-badge"
                                        style={{
                                            fontSize: '100% !important',
                                        }}
                                    >
                                        ADJ
                                    </Badge>
                                )}
                            </div>
                            {(!permissions || !permissions.includes(ApplicationPermissions.job_edit)) && (
                                <div className="d-flex flex-column">
                                    <span
                                        id="purchaseOrderNumber"
                                        name="purchaseOrderNumber"
                                    >
                                        {purchaseOrderNumber ?? '-'}
                                    </span>
                                    {!!purchaseOrderNumberChanged && (
                                        <small className="mt-1">
                                            <strong className="mr-1 text-muted">
                                                Initial PO Number:
                                            </strong>
                                            {`${initialPurchaseOrderNumber}`}
                                        </small>
                                    )}
                                </div>
                            )}
                            {!!(permissions && permissions.includes(ApplicationPermissions.job_edit)) && (
                                <div className="d-flex flex-column">
                                    <Flex alignItems="center">
                                        <Input
                                            autoComplete="off"
                                            id="purchaseOrderNumber"
                                            maxLength="44"
                                            displayMaxLength={false}
                                            className={cls(
                                                'p-1 form-control-sm w-auto',
                                            )}
                                            disabled={
                                                disableEdit
                                            }
                                            name="purchaseOrderNumber"
                                            value={purchaseOrderNumber ?? ''}
                                            onChange={
                                                onPOChanged
                                            }
                                        />
                                    </Flex>
                                    {!!purchaseOrderNumberChanged && (
                                        <small className="mt-1">
                                            <strong className="mr-1 text-muted">
                                                Initial PO Number:
                                            </strong>
                                            {`${initialPurchaseOrderNumber}`}
                                        </small>
                                    )}
                                </div>
                            )}
                        </Flex>
                    )
                }
                {
                    /* TODO - Check for contract setting for foreman capture on self-dispatching timesheets */
                    !isSelfDispatching && !enableForemanEditing && (
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="foremanName"
                                label={t('timesheet:foreman')}
                            />
                            <span
                                id="foremanName"
                                name="foremanName"
                            >
                                {foremanNameFormatted}
                            </span>
                        </Flex>
                    )
                }
                {
                    /* TODO - Check for contract setting for foreman capture on self-dispatching timesheets */
                    !isSelfDispatching && !!enableForemanEditing && (
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="foremanId"
                                label={t('timesheet:foreman')}
                            />
                            <div className="adj-indicator">
                                {!!foremanChanged && (
                                    <Badge
                                        title="This foreman has been adjusted."
                                        color="warning"
                                        className="adj-badge"
                                        style={{
                                            fontSize: '100% !important',
                                        }}
                                    >
                                        ADJ
                                    </Badge>
                                )}
                            </div>
                            <div className="d-flex flex-column">
                                <ValidatedSelect
                                    hideClear
                                    id="foreman"
                                    name="foremanId"
                                    formGroupClassName="mb-0"
                                    options={customerContactsOptions}
                                    value={
                                        (
                                            customerContactsOptions
                                            ?? []
                                        ).find(
                                            (x) => x.value === foremanId,
                                        ) ?? ''
                                    }
                                    onChange={
                                        this.props.onForemanChanged
                                    }
                                    required
                                    error="A foreman selection is required."
                                    isDisabled={false}
                                />
                                {!!foremanChanged && (
                                    <small className="mt-1">
                                        <strong className="mr-1 text-muted">
                                            Initial Foreman:
                                        </strong>
                                        {`${customerContactsOptions?.find(
                                            (x) => x.value
                                                === initialForemanId,
                                        )?.label
                                            }`}
                                    </small>
                                )}
                            </div>
                            {!!foremanId && (
                                <div className="w-100 ml-2 d-flex align-items-center">
                                    <FontAwesomeIcon
                                        icon={faCaretRight}
                                        className="text-danger mr-1"
                                    />
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="site-link"
                                        onClick={() => this.props.onEditContact(
                                            'foremanId',
                                        )}
                                    >
                                        Edit Foreman Contact Info
                                    </Button>
                                </div>
                            )}
                        </Flex>
                    )
                }
                {
                    /* TODO - Check for contract setting for requestor capture on self-dispatching timesheets */
                    !isSelfDispatching && !enableRequestorEditing && (
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="requestorName"
                                label={t('timesheet:requestor')}
                            />
                            <span
                                id="requestorName"
                                name="requestorName"
                            >
                                {requestorNameFormatted}
                            </span>
                        </Flex>
                    )
                }
                {
                    /* TODO - Check for contract setting for requestor capture on self-dispatching timesheets */
                    !isSelfDispatching && !!enableRequestorEditing && (
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="requestorId"
                                label={t('timesheet:requestor')}
                            />
                            <div className="adj-indicator">
                                {!!requestorChanged && (
                                    <Badge
                                        title="This requestor has been adjusted."
                                        color="warning"
                                        className="adj-badge"
                                        style={{
                                            fontSize: '100% !important',
                                        }}
                                    >
                                        ADJ
                                    </Badge>
                                )}
                            </div>
                            <div className="d-flex flex-column">
                                <ValidatedSelect
                                    hideClear
                                    id="requestor"
                                    name="requestorId"
                                    formGroupClassName="mb-0"
                                    options={customerContactsOptions}
                                    value={
                                        (
                                            customerContactsOptions
                                            ?? []
                                        ).find(
                                            (x) => x.value === requestorId,
                                        ) ?? ''
                                    }
                                    onChange={
                                        this.props.onRequestorChanged
                                    }
                                    required
                                    error="A requestor selection is required."
                                    isDisabled={false}
                                />
                                {!!requestorChanged && (
                                    <small className="mt-1">
                                        <strong className="mr-1 text-muted">
                                            Initial Requestor:
                                        </strong>
                                        {`${customerContactsOptions?.find(
                                            (x) => x.value
                                                === initialRequestorId,
                                        )?.label
                                            }`}
                                    </small>
                                )}
                            </div>
                            {!!requestorId && (
                                <div className="w-100 ml-2 d-flex align-items-center">
                                    <FontAwesomeIcon
                                        icon={faCaretRight}
                                        className="text-danger mr-1"
                                    />
                                    <Button
                                        color="link"
                                        size="sm"
                                        className="site-link"
                                        onClick={() => this.props.onEditContact(
                                            'requestorId',
                                        )}
                                    >
                                        Edit Requestor Contact Info
                                    </Button>
                                </div>
                            )}
                        </Flex>
                    )
                }

                {
                    !isSelfDispatching && !!enableJobTypeEditing && jobTypeId !== JobTypes.Historic && (
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="jobTypeId"
                                label="Job Type"
                            />
                            <div className="adj-indicator">
                                {!!jobTypeChanged && (
                                    <Badge
                                        title="This job type has been adjusted."
                                        color="warning"
                                        className="adj-badge"
                                        style={{
                                            fontSize: '100% !important',
                                        }}
                                    >
                                        ADJ
                                    </Badge>
                                )}
                            </div>
                            <div className="d-flex flex-column">
                                <ValidatedSelect
                                    hideClear
                                    id="jobType"
                                    name="jobTypeId"
                                    formGroupClassName="mb-0"
                                    options={jobTypeOptions}
                                    value={
                                        (
                                            jobTypeOptions
                                            ?? []
                                        ).find(
                                            (x) => x.value === jobTypeId,
                                        ) ?? ''
                                    }
                                    onChange={
                                        this.props.onJobTypeChanged
                                    }
                                    isDisabled={false}
                                />
                                {!!jobTypeChanged && (
                                    <small className="mt-1">
                                        <strong className="mr-1 text-muted">
                                            Initial Job Type:
                                        </strong>
                                        {`${jobTypeOptions?.find(
                                            (x) => x.value
                                                === initialJobTypeId,
                                        )?.label
                                            }`}
                                    </small>
                                )}
                            </div>
                        </Flex>
                    )
                }
                {Boolean(this.props.contractUnionWorkType?.id) && (
                    <Flex alignItems="center">
                        <FormLabel
                            htmlFor="contractUnionWorkType"
                            label="Work type"
                        />
                        <span
                            id="contractUnionWorkType"
                            name="contractUnionWorkType"
                            readOnly
                        >
                            {this.props.contractUnionWorkType?.description}
                        </span>
                    </Flex>
                )}

                {Boolean(this.props.contractSchedule) && (
                    <div>
                        <Flex alignItems="center">
                            <FormLabel
                                htmlFor="contractSchedule"
                                label="Schedule"
                            />

                            <span
                                id="contractSchedule"
                                name="contractSchedule"
                            >
                                {this.props.contractSchedule}
                            </span>
                        </Flex>


                        {Boolean(this.props.canDesignateMakeupJob) && (permissions && (!!permissions.includes(
                            ApplicationPermissions.job_edit,
                        )
                            || !!permissions.includes(
                                ApplicationPermissions.payroll_edit,
                            )
                            || !!permissions.includes(
                                ApplicationPermissions.billing_edit,
                            )))
                            && isTimesheet && (
                                <Flex alignItems="center">
                                    <div>
                                        <FormCheckbox
                                            small
                                            className="ml-3"
                                            id="isDesignatedMakeupJob"
                                            name="isDesignatedMakeupJob"
                                            checked={this.props.isDesignatedMakeupJob}
                                            onChange={this.props.handleDesignatedMakeupJobChange}
                                            labelText="This is a designated make-up job"
                                            disabled={!(timesheetStatusId
                                                !== TimesheetStatus.Approved)
                                                || (isPayroll
                                                    && (payrollStatusId == 1
                                                        || payrollStatusId == 3))
                                                || (isBilling
                                                    && (billingStatusId == 1
                                                        || billingStatusId == 3))}
                                        />
                                    </div>
                                </Flex>
                            )}
                    </div>

                )}

                <Flex alignItems="center">
                    <FormLabel
                        htmlFor="notes"
                        label={t('timesheet:notes')}
                    />

                    <div>
                        {jobNotes ?? '-'}
                    </div>
                </Flex>

                <Flex alignItems="center">
                    <div
                        className="d-flex"
                        style={{ marginTop: '12px' }}
                    >
                        {!isSelfDispatching
                            && permissions && (!!permissions.includes(
                                ApplicationPermissions.job_edit,
                            )
                                || !!permissions.includes(
                                    ApplicationPermissions.payroll_edit,
                                )
                                || !!permissions.includes(
                                    ApplicationPermissions.billing_edit,
                                ))
                            && ((isTimesheet
                                && timesheetStatusId
                                !== TimesheetStatus.Approved)
                                || (isPayroll
                                    && (payrollStatusId == 1
                                        || payrollStatusId == 3))
                                || (isBilling
                                    && (billingStatusId == 1
                                        || billingStatusId == 3)))
                            && canEmergency !== false
                            && (
                                <Button
                                    color="link"
                                    className="site-link no-ellipsis mr-2"
                                    style={!isEmergencyOverall ? { color: 'blue' } : {}}
                                    onClick={() => this.showEmergencyConfirmModal()}
                                >
                                    <FontAwesomeIcon
                                        icon={faAmbulance}
                                        className="mr-1"
                                    />
                                    Emergency
                                    {isEmergencyOverall
                                        ? ` - ${emergencyEmployeeName}`
                                        : ''}
                                </Button>
                            )}
                        {isAdmin
                            && !isSelfDispatching
                            && isTimesheet
                            && !isSchedulingErrorCancelled
                            && !isClientCancelled
                            && !isJobCancelled
                            && (timesheetStatusId
                                == TimesheetStatus.Submitted
                                || timesheetStatusId
                                == TimesheetStatus.BillingPayrollRejected) && (
                                <Button
                                    color="link"
                                    className="site-link no-ellipsis"
                                    style={{
                                        color: 'blue',
                                    }}
                                    onClick={() => this.showCancelJobModal()}
                                >
                                    <FontAwesomeIcon
                                        icon={faTimesCircle}
                                        className="mr-1"
                                    />
                                    Client Cancel
                                </Button>
                            )}
                        {isAdmin
                            && !isSelfDispatching
                            && isTimesheet
                            && !isSchedulingErrorCancelled
                            && isClientCancelled
                            && isJobCancelled
                            && (timesheetStatusId
                                == TimesheetStatus.Submitted
                                || timesheetStatusId
                                == TimesheetStatus.BillingPayrollRejected) && (
                                <Button
                                    color="link"
                                    className="site-link"
                                    style={{
                                        color: 'red',
                                        marginLeft: '4px',
                                    }}
                                    onClick={() => this.showUndoClientCancelModal()}
                                >
                                    <FontAwesomeIcon
                                        icon={faTimesCircle}
                                        className="mr-1"
                                    />
                                    Undo Client Cancel
                                </Button>
                            )}
                        {showCustomForms && !!jobId && (
                            <CustomForms
                                className="ml-2"
                                tooltipId="job-forms-incomplete-icon"
                                jobId={jobId}
                                formDate={jobStartTimeRaw}
                                isEmergency={isEmergencyJob}
                                formContextId={CustomFormContext.Job}
                                onOpen={() => this.props.onCustomFormToggle?.(true)}
                                onClose={() => this.props.onCustomFormToggle?.(false)}
                            />
                        )}
                    </div>
                </Flex>

                {!isSelfDispatching
                    && ((isTimesheet
                        && timesheetStatusId == TimesheetStatus.Approved)
                        || (isPayroll
                            && (payrollStatusId == 2
                                || payrollStatusId == 4))
                        || (isBilling
                            && (billingStatusId == 2
                                || billingStatusId == 4))
                        || isTimesheetForm)
                    && isEmergencyOverall && (
                        <Flex alignItems="center">
                            <div className="d-flex flex-column flex-fill">
                                <span className="text-danger">
                                    *** Emergency ***
                                </span>
                            </div>
                        </Flex>
                    )
                }

            </div>
        );
    }
}

export const JobDetails = withTranslation()(JobDetailsBase);
